import { Metamodel } from 'app/model/base/model';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { TransformationAdapter } from 'app/model/adapters/dataflow/transformations/base-transformation.adapter';
import { AssertTransformation } from 'app/model/dataflow/asserttransformation';
import { DataFlowExpressionUtils } from '../expression.util';
import { isTruthy } from 'app/model/dataflow/dataflow-expression-utilities';
import { BooleanDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { FLOW } from '../IDataflow';
import { AssertExpressionType } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AssertTransformation),
    asynchronous: true
})
export class AssertTransformationAdapter extends TransformationAdapter<AssertTransformation> {
    public async getDslScript(assertTransformation: AssertTransformation): Promise<string> {
        const props = assertTransformation.assertExpressions.map((assertExpression) => {
            const assertType = assertExpression.assertType;
            let expression = '';
            switch (assertType) {
                case AssertExpressionType.ExpectTrue:
                    expression = DataFlowExpressionUtils.wrapExpressionWithEmptyValue(
                        assertExpression.trueExpression.expression
                    );
                    break;
                case AssertExpressionType.ExpectUnique:
                    let count = 0;
                    assertExpression.uniqueExpressions.forEach((uniqueExpression) => {
                        expression += DataFlowExpressionUtils.wrapExpressionWithEmptyValue(uniqueExpression.expression);
                        count++;
                        if (count < assertExpression.uniqueExpressions.length) {
                            expression += ', ';
                        }
                    });
                    break;
                case AssertExpressionType.ExpectExists:
                    const leftExpression = DataFlowExpressionUtils.wrapExpressionWithEmptyValue(
                        assertExpression.existsLeftExpression.expression
                    );
                    const rightExpression = DataFlowExpressionUtils.wrapExpressionWithEmptyValue(
                        assertExpression.existsRightExpression.expression
                    );
                    expression = `${leftExpression} == ${rightExpression}`;
                    break;
                default:
                    break;
            }
            const ignoreNulls = assertExpression.ignoreNulls ? 'true' : 'false';
            const assertId = assertExpression.assertId;
            const filter =
                assertExpression.filterExpression && assertExpression.filterExpression.expression
                    ? DataFlowExpressionUtils.wrapExpressionWithEmptyValue(assertExpression.filterExpression.expression)
                    : null;
            const assertDescription =
                assertExpression.assertDescription && assertExpression.assertDescription.expression
                    ? DataFlowExpressionUtils.wrapExpressionWithEmptyValue(
                          assertExpression.assertDescription.expression
                      )
                    : null;

            if (assertDescription !== null) {
                return `${assertType}(${expression}, ${ignoreNulls}, \'${assertId}\', ${filter}, ${assertDescription})`;
            }
            if (filter !== null) {
                return `${assertType}(${expression}, ${ignoreNulls}, \'${assertId}\', ${filter})`;
            }
            return `${assertType}(${expression}, ${ignoreNulls}, \'${assertId}\')`;
        });

        if (isTruthy(assertTransformation.abort)) {
            props.push(
                `abort: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    assertTransformation.abort,
                    BooleanDataFlowType.TYPE
                )}`
            );
        }

        const partition = TransformationAdapter.getPartitionDslScript(assertTransformation);

        const knownProps = [...props, partition].filter((c) => !!c).join(',\n\t');
        const unknownProps = this._getUnknownProps(assertTransformation, knownProps);
        const transformationData = [knownProps, unknownProps].filter((c) => !!c).join(',\n\t');

        return `${this._getInputStreams(assertTransformation)} ${
            AssertTransformation.MODEL.name
        }(${transformationData}) ${FLOW} ${this._getOutputStreams(assertTransformation)}`;
    }
}
